/**
 * Hook para buscar eventos (timeline) de uma instância de execução.
 * Suporta paginação por cursor (carregar mais eventos históricos).
 */
import { useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getInstanceEvents } from '../services/instanceDetailService';
import type { InstanceEvent } from '../types';
import { executionKeys } from '../keys';

const PAGE_SIZE = 50;

export function useInstanceEvents(organizationId?: string, instanceId?: string) {
  const queryClient = useQueryClient();
  const [allEvents, setAllEvents] = useState<InstanceEvent[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const query = useQuery({
    queryKey: [...executionKeys.detail(organizationId ?? '', instanceId ?? ''), 'events'],
    enabled: Boolean(organizationId && instanceId),
    queryFn: async () => {
      const result = await getInstanceEvents(organizationId!, instanceId!, PAGE_SIZE);
      setAllEvents(result.events);
      setHasMore(result.hasMore);
      setCursor(result.nextCursor);
      return result;
    },
    staleTime: 15_000,
  });

  const loadMore = useCallback(async () => {
    if (!organizationId || !instanceId || !hasMore || isLoadingMore || !cursor) return;
    setIsLoadingMore(true);
    try {
      const result = await getInstanceEvents(organizationId, instanceId, PAGE_SIZE, cursor);
      setAllEvents(prev => [...prev, ...result.events]);
      setHasMore(result.hasMore);
      setCursor(result.nextCursor);
    } finally {
      setIsLoadingMore(false);
    }
  }, [organizationId, instanceId, hasMore, isLoadingMore, cursor]);

  return {
    events: allEvents,
    isLoading: query.isLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    error: query.error as Error | null,
    refetch: () => {
      setAllEvents([]);
      setCursor(undefined);
      setHasMore(false);
      return query.refetch();
    },
  };
}

